import React from 'react';
import { observer } from 'src/utils/mobx-react';
import Icon from 'src/components/Icon/Icon';
import { CurrencyType, formatAmountWithCurrency } from 'src_common/common/amount/website-money/currency';
import { useAppStateContext } from 'src/AppState/AppState';
import { DateTime } from 'src_common/utils/time/time';

type FreeBetsHistoryPropsType = {
    currency: CurrencyType;
    customerId: number;
};

const defaultDateFormat = 'DD/MM/YY';

type MappedStatusType = 'betting' | 'disabled';

const MAPPED_STATUS = {
    betting: 'Claimed',
    disabled: 'Deleted',
} as const;

function isStringInMappedStatus(value: string): value is MappedStatusType {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return Object.keys(MAPPED_STATUS).includes(value ?? '');
}

export const pickMappedStatus = (status: string): string => {
    return isStringInMappedStatus(status) ? MAPPED_STATUS[status] : status;
};

export const FreeBetsHistory = observer('FreeBetsHistory', ({ customerId, currency }: FreeBetsHistoryPropsType) => {
    const { bankingTabState } = useAppStateContext();
    const state = bankingTabState.get(customerId);
    const freebetsHistory = state.freebetsList.get();
    const historyIsEmpty = freebetsHistory.type === 'ready' && freebetsHistory.value.history.length === 0;

    return (
        <div className='freebets-history'>
            <table key='free-bets-history-table'>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Promotion / Source</th>
                        <th className='align-right'>Amount</th>
                        <th className='align-right'>Status</th>
                        <th className='align-right'>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {historyIsEmpty ? (
                        <div
                            className='info-message'
                            key='info'
                        >
                            No free bets history for this period of time
                        </div>
                    ) : null}

                    {freebetsHistory.type === 'ready'
                        ? freebetsHistory.value.history.map(({ body, creditId, date, promotion }, index) => {
                              if (body === null || body === undefined) {
                                  return;
                              }

                              const expiryDate = body.changeBody.expiryDate ?? null;
                              const reason = body.reason ?? null;
                              const amount =
                                  body.type === 'disabled' ? body.newCreditAmount : body.changeBody.amountDelta;
                              const newWalletAmount = body.newWalletAmount ?? null;

                              return (
                                  <tr key={`${creditId}${expiryDate ?? ''}${index}`}>
                                      <td data-test='free-bet-history-date'>
                                          {DateTime.from(date)?.format(defaultDateFormat)}
                                      </td>
                                      <td data-test='free-bet-history-promotion/source'>
                                          <span key='promotion-name'> {promotion}</span>
                                          {body.type !== 'created' || expiryDate === null ? null : (
                                              <span
                                                  key='expiry-date'
                                                  className='small-text'
                                              >
                                                  Expires: {DateTime.from(expiryDate)?.format(defaultDateFormat)}
                                              </span>
                                          )}
                                      </td>
                                      <td
                                          className='align-right'
                                          data-test='free-bet-history-amount'
                                      >
                                          {body.type === 'disabled' ? '- ' : ''}
                                          {amount !== undefined && amount !== null
                                              ? formatAmountWithCurrency(
                                                    currency,
                                                    $appState.configState.precision.newFromOld(amount)
                                                )
                                              : '-'}
                                      </td>

                                      <td
                                          className='align-right text-capitalize'
                                          data-test='free-bet-history-status'
                                      >
                                          <span key='type'>{pickMappedStatus(body.type)}</span>
                                          {body.type === 'disabled' && reason !== null ? (
                                              <span title={reason}>
                                                  <Icon
                                                      name='info'
                                                      key='icon-status'
                                                  />
                                              </span>
                                          ) : null}
                                      </td>
                                      <td
                                          className='align-right'
                                          data-test='free-bet-history-balance'
                                      >
                                          {newWalletAmount === null
                                              ? '-'
                                              : formatAmountWithCurrency(
                                                    currency,
                                                    $appState.configState.precision.newFromOld(newWalletAmount)
                                                )}
                                      </td>
                                  </tr>
                              );
                          })
                        : null}
                </tbody>
            </table>
            {freebetsHistory.type === 'loading' ? (
                <div
                    className='spinner spinner--inline'
                    key='spinner'
                ></div>
            ) : null}
        </div>
    );
});
